import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Trophy, LogOut, User } from "lucide-react";
import { useAuth } from "../contexts/AuthContext.jsx";
import { winnersPublicAPI } from "../services/api";
import { useSubmissionsOpen } from "../hooks/useSubmissionsOpen";
import SkipLink from "./SkipLink";

const Navigation = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isCompetitionOpen, setIsCompetitionOpen] = useState(false);
  const [winnersRevealed, setWinnersRevealed] = useState(false);
  const { open: submissionsOpen } = useSubmissionsOpen();
  const location = useLocation();
  const navigate = useNavigate();
  const timeoutRef = useRef(null);
  const { user, isAuthenticated, logout } = useAuth();

  // Winners link appears ONLY once winners are publicly revealed (anti-leak:
  // the hub endpoint returns revealed=false with no data before reveal).
  useEffect(() => {
    let alive = true;
    winnersPublicAPI.hub()
      .then((r) => { if (alive) setWinnersRevealed(!!r.data?.revealed); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  // Debug log
  console.log('Navigation - Auth state:', { user: user?.name, isAuthenticated });

  const isActive = (path) => location.pathname === path;

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleCompetitionEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsCompetitionOpen(true);
  };

  const handleCompetitionLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setIsCompetitionOpen(false);
    }, 200); // 200ms delay as recommended
  };

  // Extract first name from full name for display
  const getFirstName = (fullName) => {
    if (!fullName) return '';
    return fullName.split(' ')[0];
  };

  // Hide public navigation on judge-only routes (isolated judge layout)
  if (location.pathname.startsWith('/judge') && location.pathname !== '/judges') {
    return null;
  }

  return (
    <>
      <SkipLink />
      <nav className="bg-slate-800/95 backdrop-blur-sm border-b border-orange-900/20 sticky top-0" role="navigation" aria-label="Main navigation" style={{ zIndex: 1000 }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-28">
          {/* Logo horizontal rectangulaire pour navigation */}
          <div className="flex items-center">
            <Link to="/">
              <img 
                src="/images/wuma-logo-horizontal.png" 
                alt="World Underwater Macro Awards 2026"
                width="270"
                height="180"
                className="object-contain"
                loading="eager"
              />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <Link
              to="/"
              className={`text-sm font-medium transition-colors hover:text-orange-400 ${
                isActive("/") ? "text-orange-400" : "text-gray-300"
              }`}
            >
              Home
            </Link>

            <Link
              to="/about"
              className={`text-sm font-medium transition-colors hover:text-orange-400 ${
                isActive("/about") ? "text-orange-400" : "text-gray-300"
              }`}
            >
              About Us
            </Link>

            {/* Competition Dropdown */}
            <div className="relative">
              <button
                onMouseEnter={handleCompetitionEnter}
                onMouseLeave={handleCompetitionLeave}
                className="flex items-center text-sm font-medium transition-colors hover:text-orange-400 text-gray-300"
                aria-expanded={isCompetitionOpen}
                aria-haspopup="true"
                aria-label="Competition menu"
              >
                Competition
                <ChevronDown className="ml-1 h-3 w-3" aria-hidden="true" />
              </button>
              
              {/* Custom Dropdown Menu */}
              {isCompetitionOpen && (
                <div 
                  className="absolute top-full left-0 mt-1 w-80 bg-slate-800 border border-orange-900/20 rounded-lg shadow-2xl"
                  role="menu"
                  aria-label="Competition submenu"
                  onMouseEnter={handleCompetitionEnter}
                  onMouseLeave={handleCompetitionLeave}
                  style={{ 
                    zIndex: 1001,
                    position: 'absolute',
                    top: '100%',
                    left: '0'
                  }}
                >
                  <div className="p-4">
                    {/* Current Competition */}
                    <Link
                      to="/competition/2026"
                      className="block p-4 rounded-lg bg-gradient-to-r from-orange-900 to-orange-800 hover:from-orange-800 hover:to-orange-700 transition-all duration-200"
                    >
                      <div className="flex items-center mb-2">
                        <Trophy className="h-5 w-5 text-orange-400 mr-2" />
                        <span className="text-lg font-medium text-white">WUMA</span>
                      </div>
                      <p className="text-sm text-orange-100">Submissions closed — Judging in progress</p>
                    </Link>
                    
                    {/* Other Menu Items */}
                    <div className="mt-3 space-y-1">
                      <Link
                        to="/categories"
                        className="block px-3 py-2 rounded-md text-gray-300 hover:bg-slate-700 hover:text-orange-400 transition-colors"
                      >
                        <div className="font-medium">Categories</div>
                        <div className="text-sm text-gray-400">Competition categories and requirements</div>
                      </Link>
                      
                      <Link
                        to="/rules"
                        className="block px-3 py-2 rounded-md text-gray-300 hover:bg-slate-700 hover:text-orange-400 transition-colors"
                      >
                        <div className="font-medium">Rules & Guidelines</div>
                        <div className="text-sm text-gray-400">Competition terms and submission guidelines</div>
                      </Link>
                      
                      {/* Submit Photo — hidden once submissions are closed */}
                      {submissionsOpen && (
                        <Link
                          to="/submit"
                          className="block px-3 py-2 rounded-md text-gray-300 hover:bg-slate-700 hover:text-orange-400 transition-colors"
                        >
                          <div className="font-medium">Submit Photo</div>
                          <div className="text-sm text-gray-400">Upload your underwater macro photography</div>
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <Link
              to="/judges"
              className={`text-sm font-medium transition-colors hover:text-orange-400 ${
                isActive("/judges") ? "text-orange-400" : "text-gray-300"
              }`}
            >
              Judges
            </Link>

            <Link
              to="/awards"
              className={`text-sm font-medium transition-colors hover:text-orange-400 ${
                isActive("/awards") ? "text-orange-400" : "text-gray-300"
              }`}
            >
              Awards & Prizes
            </Link>

            <Link
              to="/gallery"
              className={`text-sm font-medium transition-colors hover:text-orange-400 ${
                isActive("/gallery") ? "text-orange-400" : "text-gray-300"
              }`}
            >
              Gallery
            </Link>

            {winnersRevealed && (
              <Link
                to="/winners"
                data-testid="nav-winners-link"
                className={`text-sm font-medium transition-colors hover:text-amber-400 ${
                  location.pathname.startsWith("/winners") ? "text-amber-400" : "text-gray-300"
                }`}
              >
                Winners
              </Link>
            )}

            <Link
              to="/contact"
              className={`text-sm font-medium transition-colors hover:text-orange-400 ${
                isActive("/contact") ? "text-orange-400" : "text-gray-300"
              }`}
            >
              Contact
            </Link>

            {/* Auth Buttons */}
            <div className="flex items-center space-x-3 ml-6">
              {isAuthenticated ? (
                <>
                  {/* Admin Dashboard Link (if admin) */}
                  {user?.is_admin && (
                    <Link
                      to="/super-admin"
                      className="flex items-center space-x-1 text-sm font-medium text-orange-400 hover:text-orange-500 transition-colors"
                    >
                      <Trophy className="w-4 h-4" />
                      <span>Admin</span>
                    </Link>
                  )}
                  
                  {/* Profile Link with First Name */}
                  <Link
                    to={`/profile/${user?.id}`}
                    className="flex items-center space-x-1 text-sm font-medium text-gray-300 hover:text-orange-400 transition-colors"
                  >
                    <User className="w-4 h-4" />
                    <span>{getFirstName(user?.name)}</span>
                  </Link>
                  
                  {/* Logout Button */}
                  <button
                    onClick={handleLogout}
                    className="flex items-center space-x-1 text-sm font-medium text-gray-300 hover:text-orange-400 transition-colors"
                  >
                    <LogOut className="w-4 h-4" />
                    <span>Logout</span>
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="text-sm font-medium text-gray-300 hover:text-orange-400 transition-colors"
                  >
                    Login
                  </Link>
                  <Link
                    to="/register"
                    className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
                  >
                    Register
                  </Link>
                </>
              )}
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-gray-300 hover:text-orange-400 transition-colors p-2"
              aria-label={isMenuOpen ? "Close navigation menu" : "Open navigation menu"}
              aria-expanded={isMenuOpen}
              aria-controls="mobile-menu"
              type="button"
            >
              {isMenuOpen ? <X className="w-6 h-6" aria-hidden="true" /> : <Menu className="w-6 h-6" aria-hidden="true" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        {isMenuOpen && (
          <div className="md:hidden" id="mobile-menu" role="menu" aria-label="Mobile navigation menu">
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-slate-800/95 rounded-lg mt-2">
              <Link
                to="/"
                className="block px-3 py-2 text-gray-300 hover:text-orange-400 transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                Home
              </Link>
              <Link
                to="/about"
                className="block px-3 py-2 text-gray-300 hover:text-orange-400 transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                About Us
              </Link>
              
              {/* Competition Section */}
              <Link
                to="/competition/2026"
                className="block px-3 py-2 text-gray-300 hover:text-orange-400 transition-colors font-medium"
                onClick={() => setIsMenuOpen(false)}
              >
                Competition
              </Link>
              <Link
                to="/categories"
                className="block px-3 py-2 text-gray-300 hover:text-orange-400 transition-colors ml-4"
                onClick={() => setIsMenuOpen(false)}
              >
                Categories
              </Link>
              <Link
                to="/rules"
                className="block px-3 py-2 text-gray-300 hover:text-orange-400 transition-colors ml-4"
                onClick={() => setIsMenuOpen(false)}
              >
                Rules & Guidelines
              </Link>
              {/* Submit Photo — hidden once submissions are closed */}
              {submissionsOpen && (
                <Link
                  to="/submit"
                  className="block px-3 py-2 text-gray-300 hover:text-orange-400 transition-colors ml-4"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Submit Photo
                </Link>
              )}
              
              <Link
                to="/judges"
                className="block px-3 py-2 text-gray-300 hover:text-orange-400 transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                Judges
              </Link>
              <Link
                to="/awards"
                className="block px-3 py-2 text-gray-300 hover:text-orange-400 transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                Awards & Prizes
              </Link>
              <Link
                to="/gallery"
                className="block px-3 py-2 text-gray-300 hover:text-orange-400 transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                Gallery
              </Link>
              {winnersRevealed && (
                <Link
                  to="/winners"
                  data-testid="nav-winners-link-mobile"
                  className="block px-3 py-2 text-gray-300 hover:text-amber-400 transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Winners
                </Link>
              )}
              <Link
                to="/contact"
                className="block px-3 py-2 text-gray-300 hover:text-orange-400 transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                Contact
              </Link>
              
              {/* Mobile Auth Buttons */}
              {isAuthenticated ? (
                <>
                  {user?.is_admin && (
                    <Link
                      to="/super-admin"
                      className="flex items-center px-3 py-2 text-orange-400 hover:text-orange-500 transition-colors"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <Trophy className="w-4 h-4 mr-2" />
                      <span>Admin Dashboard</span>
                    </Link>
                  )}
                  <Link
                    to={`/profile/${user?.id}`}
                    className="flex items-center px-3 py-2 text-gray-300 hover:text-orange-400 transition-colors"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <User className="w-4 h-4 mr-2" />
                    {getFirstName(user?.name)} - Profile
                  </Link>
                  <button
                    onClick={() => {
                      handleLogout();
                      setIsMenuOpen(false);
                    }}
                    className="flex items-center px-3 py-2 text-gray-300 hover:text-orange-400 transition-colors"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="block px-3 py-2 text-gray-300 hover:text-orange-400 transition-colors"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Login
                  </Link>
                  <Link
                    to="/register"
                    className="block px-3 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-md transition-colors mx-3 text-center"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Register
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
    </>
  );
};

export default Navigation;